import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, switchMap } from 'rxjs';
import { ActivityPolicy } from 'src/app/reporting/models/security/ActivityPolicy';
import { CreateReportsPolicy } from 'src/app/reporting/models/security/CreateReportsPolicy';
import { TabsPolicy } from 'src/app/reporting/models/security/TabsPolicy';
import { v4 as uuidv4 } from 'uuid';
import {
  FORTRESS_CREATE_REPORTS,
  FORTRESS_SALES_REPORTING_REGEX,
  SECURITY_POLICIES_PATH,
} from '../ReportingServiceConstants';
import { IPolicyElement } from 'src/app/reporting/models/security/IPolicyElement';
import { PolicyElement } from 'src/app/reporting/models/security/PolicyElement';
import { BenchmarkPolicy } from 'src/app/reporting/models/security/BenchmarkPolicy';
import { FUNDS_REPORTING_BASE_PATH } from 'src/app/constants/NextGenConstants';

/**
 * AuthService handles authentication state, user info, and user policy management.
 * It provides methods for login, logout, and user info retrieval.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** Indicates if the user is currently authenticated. */
  private isLoggedIn: boolean = false;

  /** Store the current user's policies. */
  private readonly policyElements: IPolicyElement[] = [];

  // The 'SALES REPORTING.CREATE REPORTS' policy element
  private readonly createReportsPolicyElement = new PolicyElement(
    FORTRESS_CREATE_REPORTS,
  );

  // The 'SALES REPORTING.EXTERNAL USER' policy element
  private readonly externalUserPolicyElement = new PolicyElement(
    'EXTERNAL USER',
  );

  private readonly tabsPolicy = new TabsPolicy();
  private readonly activityPolicy = new ActivityPolicy();
  private readonly benchmarkPolicy = new BenchmarkPolicy();
  private readonly createReportsPolicy = new CreateReportsPolicy();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {
    this.policyElements.push(
      this.createReportsPolicyElement,
      this.externalUserPolicyElement,
      this.tabsPolicy,
      this.activityPolicy,
      this.benchmarkPolicy,
      this.createReportsPolicy,
    );
  }

  /**
   * Returns true if the user is authenticated, false otherwise.
   */
  isUserAuthenticated(): boolean {
    return this.isLoggedIn;
  }

  /**
   * Logs out the user and resets authentication state and policy.
   */
  logout(): void {
    this.isLoggedIn = false;
    this.policyElements.forEach((policy) => {
      policy.reset();
    });
  }
  /**
   * Attempts to log in the user by fetching user policy from the server.
   * @returns Observable<boolean> indicating login success.
   */
  login(): Observable<boolean> {
    this.logout();
    return this.http
      .post<{
        policyElements: string[];
      }>(SECURITY_POLICIES_PATH, null, { headers: this.contentTypeHeader })
      .pipe(
        map((response) => {
          if (!response.policyElements?.length) {
            return false;
          }

          // Apply policy elements to policies
          for (const rawPolicyElement of response.policyElements) {
            // Remove 'SALES REPORTING.'
            const trimmedName = rawPolicyElement.replace(
              FORTRESS_SALES_REPORTING_REGEX,
              '',
            );
            for (const policyElement of this.policyElements) {
              if (policyElement.apply(trimmedName)) break;
            }
          }

          return true;
        }),
        catchError(() => of(false)),
        switchMap((response) => {
          // If no 'SALES REPORTING.CREATE REPORTS' policy element
          if (!response || !this.createReportsPolicyElement.value) {
            this.router.navigate([`${FUNDS_REPORTING_BASE_PATH}/error`]);
            return EMPTY;
          }

          this.isLoggedIn = true;
          this.router.navigate([FUNDS_REPORTING_BASE_PATH], {
            // Default behavior; added for readability
            onSameUrlNavigation: 'ignore',
          });
          return of(true);
        }),
      );
  }

  /**
   * Return the current user policies if authenticated, otherwise a new Policy instance.
   */
  isValidCreateReportsPolicy(): boolean {
    return this.isLoggedIn ? this.createReportsPolicyElement.value : false;
  }

  isExternalUser(): boolean {
    return this.isLoggedIn ? this.externalUserPolicyElement.value : false;
  }

  getTabsPolicy(): TabsPolicy {
    return this.isLoggedIn ? this.tabsPolicy : new TabsPolicy();
  }

  getActivityPolicy(): ActivityPolicy {
    return this.isLoggedIn ? this.activityPolicy : new ActivityPolicy();
  }

  getBenchmarkPolicy(): BenchmarkPolicy {
    return this.isLoggedIn ? this.benchmarkPolicy : new BenchmarkPolicy();
  }

  getCreateReportsPolicy(): CreateReportsPolicy {
    return this.isLoggedIn
      ? this.createReportsPolicy
      : new CreateReportsPolicy();
  }

  /**
   * Returns HTTP headers with content type and a unique tracking ID.
   */
  private get contentTypeHeader(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'FID-LOG-TRACKING-ID': uuidv4(),
    });
  }
}
